/**
 * ProposalCard — Phase G4A CP2.
 *
 * Single row in the `/proposals` library grid. Displays:
 *   - Title (linked to `/proposals/{id}/edit`)
 *   - Optional grant reference chip
 *   - Status pill
 *   - Relative "updated_at" string
 *   - Quick actions: Continue / Duplicate / Archive / Delete
 *
 * Delete is guarded by an inline confirm-state on the card
 * (`confirming === true`) — non-draft proposals hide the Delete
 * button entirely (backend returns 409 for those; guarding at the
 * UI level prevents the impossible click).
 *
 * Archive is one-click and reversible-by-duplicate; no confirm.
 * Duplicate is fully additive; no confirm.
 *
 * Voice discipline: all copy sourced from a factual vocabulary set
 * ("Continue drafting", "Duplicate", "Archive", "Delete draft").
 * No banned phrases.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Archive, Trash2, Pencil, Loader2 } from "lucide-react";
import { statusMeta, formatRelative } from "./utils";

export default function ProposalCard({
  proposal,
  onDuplicate,
  onArchive,
  onDelete,
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(null); // "duplicate" | "archive" | "delete"

  const status = proposal.status || "draft";
  const meta = statusMeta(status);
  const isDeletable = status === "draft";
  const isArchivable = status !== "archived";

  async function run(action, kind) {
    setBusy(kind);
    try {
      await action();
    } finally {
      setBusy(null);
      setConfirming(false);
    }
  }

  return (
    <div
      data-testid={`proposal-card-${proposal.id}`}
      className="rounded-3xl border border-v2-border bg-v2-surface p-6 flex flex-col gap-4 hover:border-v2-accent/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Link
            to={`/proposals/${proposal.id}/edit`}
            data-testid={`proposal-card-title-${proposal.id}`}
            className="text-v2-heading-sm font-semibold text-v2-text hover:text-v2-accent transition-colors line-clamp-2"
          >
            {proposal.title || "Untitled proposal"}
          </Link>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <span
              data-testid={`proposal-card-status-${proposal.id}`}
              className="inline-flex items-center rounded-full px-2.5 py-0.5 text-v2-caption uppercase tracking-[0.14em]"
              style={{
                background: meta.tintBg,
                border: `1px solid ${meta.tintBorder}`,
                color: meta.tint,
              }}
            >
              {meta.label}
            </span>
            {proposal.grant_id && (
              <span
                data-testid={`proposal-card-grant-ref-${proposal.id}`}
                className="text-v2-caption text-v2-textMuted"
              >
                Linked to grant #{proposal.grant_id.slice(-6)}
              </span>
            )}
            <span className="text-v2-caption text-v2-textMuted">
              Updated {formatRelative(proposal.updated_at)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center flex-wrap gap-2">
        <Link
          to={`/proposals/${proposal.id}/edit`}
          data-testid={`proposal-card-continue-${proposal.id}`}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm font-medium text-v2-accentInk bg-v2-accent hover:bg-v2-accentHover transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          Continue drafting
        </Link>
        <button
          type="button"
          data-testid={`proposal-card-duplicate-${proposal.id}`}
          onClick={() => run(() => onDuplicate(proposal), "duplicate")}
          disabled={busy !== null}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm text-v2-text bg-v2-surfaceSubtle hover:bg-v2-surface border border-v2-border transition-colors disabled:opacity-60"
        >
          {busy === "duplicate" ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Copy className="w-3.5 h-3.5" />
          )}
          Duplicate
        </button>
        {isArchivable && (
          <button
            type="button"
            data-testid={`proposal-card-archive-${proposal.id}`}
            onClick={() => run(() => onArchive(proposal), "archive")}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text bg-transparent hover:bg-v2-surfaceSubtle border border-v2-border transition-colors disabled:opacity-60"
          >
            {busy === "archive" ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Archive className="w-3.5 h-3.5" />
            )}
            Archive
          </button>
        )}
        {isDeletable && !confirming && (
          <button
            type="button"
            data-testid={`proposal-card-delete-${proposal.id}`}
            onClick={() => setConfirming(true)}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-v2-body-sm text-v2-textMuted hover:text-red-400 bg-transparent hover:bg-red-500/10 border border-v2-border transition-colors disabled:opacity-60"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        )}
        {isDeletable && confirming && (
          <div className="inline-flex items-center gap-2">
            <span className="text-v2-caption text-red-400">Delete this draft?</span>
            <button
              type="button"
              data-testid={`proposal-card-delete-confirm-${proposal.id}`}
              onClick={() => run(() => onDelete(proposal), "delete")}
              disabled={busy !== null}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-v2-caption font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
            >
              {busy === "delete" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                "Confirm"
              )}
            </button>
            <button
              type="button"
              data-testid={`proposal-card-delete-cancel-${proposal.id}`}
              onClick={() => setConfirming(false)}
              className="text-v2-caption text-v2-textMuted hover:text-v2-text"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
